import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ProgressRing } from '@/components/onboarding/ProgressRing'
import { SparkleBurst } from '@/components/onboarding/SparkleBurst'
import { tUi, type OnboardingUiLocale } from '@/components/onboarding/copy'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type {
  OnboardingChecklistFlags,
  OnboardingState,
} from '@/components/onboarding/useOnboardingState'
import { useLocale } from '@/hooks/useLocale'
import { cn } from '@/lib/utils'

export interface OnboardingChecklistItem {
  key: keyof OnboardingChecklistFlags
  label: string
  sub: string
  optional?: boolean
  href?: string
}

export interface OnboardingChecklistCardProps {
  /**
   * Full onboarding state from `useOnboardingState().state`.
   * Contract: `<OnboardingChecklistCard state onDismissForever />`.
   */
  state: OnboardingState
  /** Confirm dismiss-forever (parent opens dialog then PATCH). */
  onDismissForever?: () => void
  /** Step row tap — parent routes to the completing screen. */
  onStepTap?: (key: keyof OnboardingChecklistFlags) => void
  /** Override checklist row copy (defaults match AGT-ONB-005 table). */
  items?: OnboardingChecklistItem[]
  /** Title override. */
  title?: string
  /** Start expanded. */
  defaultExpanded?: boolean
  className?: string
}

// Ordered as the natural "get operating" flow: set up who you are and how
// leads reach you, then list, then publish. (Two more steps — connect comms
// channels, design your first post — land once their onboarding-state flags
// exist; tracked as a follow-up.)
const DEFAULT_ITEMS: OnboardingChecklistItem[] = [
  {
    key: 'profile_completed',
    label: 'Complete your profile',
    sub: 'Photo, bio, contact — your public identity',
  },
  {
    key: 'channels_connected',
    label: 'Connect your social & messaging channels',
    sub: 'Instagram, Facebook, WhatsApp — so leads reach you',
  },
  {
    key: 'notifications_enabled',
    label: 'Turn on notifications',
    sub: 'Never miss a new lead',
  },
  {
    key: 'first_listing_published',
    label: 'List & publish your first property',
    sub: '2 min via WhatsApp',
  },
  {
    key: 'subscription_active',
    label: 'Upgrade to paid',
    sub: 'Unlock unlimited listings and portal integrations',
    optional: true,
  },
]

/**
 * Persistent dashboard onboarding checklist widget.
 *
 * Used by: AGT-ONB-005 (mounted on AGT-DSH-001 Zone 3); Pro collapses to `<OnboardingPill>`.
 * Stub visual + prop types only — no PATCH.
 */
export function OnboardingChecklistCard({
  state,
  onDismissForever,
  onStepTap,
  items = DEFAULT_ITEMS,
  title,
  defaultExpanded = true,
  className,
}: OnboardingChecklistCardProps) {
  const { isArabic } = useLocale()
  const locale: OnboardingUiLocale = isArabic ? 'ar' : 'en'
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [showCompleted, setShowCompleted] = useState(false)
  const reducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  if (state.dismissed_forever) return null

  // Ring math derives from the required (non-optional) items actually shown,
  // so it always matches the list — no hardcoded key set to drift from it.
  const requiredItems = items.filter((item) => !item.optional)
  const total = requiredItems.length || 1
  const completed = requiredItems.filter((item) => state.checklist[item.key]).length
  const remaining = Math.max(0, total - completed)
  const pct = Math.round((completed / total) * 100)
  const allDone = completed >= total

  const incomplete = items.filter((item) => !state.checklist[item.key])
  const complete = items.filter((item) => state.checklist[item.key])
  const visible = showCompleted ? [...incomplete, ...complete] : incomplete

  const resolvedTitle = title ?? tUi('card.title', locale)
  const sub = allDone
    ? tUi('card.sub.done', locale)
    : tUi('card.sub.partial', locale, { pct, remaining })

  return (
    <section
      role="region"
      aria-label="Onboarding progress"
      className={cn(
        'relative rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)]',
        'bg-[var(--lc-surface-raised)] p-[var(--lc-space-lg)]',
        'shadow-[var(--lc-elevation-sm)]',
        className,
      )}
      data-onboarding-checklist
      data-completed={completed}
    >
      {allDone ? (
        <div className="absolute end-3 top-3">
          <SparkleBurst active reducedMotion={reducedMotion} />
        </div>
      ) : null}

      <div className="flex items-start gap-3">
        <ProgressRing size={44} completed={completed} total={total} />
        <div className="min-w-0 flex-1">
          <h2
            className="text-[var(--lc-text-heading)]"
            style={{ font: 'var(--lc-type-heading-3)' }}
          >
            {resolvedTitle}
          </h2>
          <p
            className="text-[var(--lc-text-secondary)]"
            style={{ font: 'var(--lc-type-body-sm)' }}
          >
            {sub}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-expanded={expanded}
          aria-label={expanded ? tUi('card.collapse', locale) : tUi('card.expand', locale)}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      </div>

      {expanded ? (
        <ul className="mt-[var(--lc-space-md)] flex flex-col gap-2">
          {complete.length > 0 && !showCompleted ? (
            <li>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0"
                onClick={() => setShowCompleted(true)}
              >
                {tUi('card.showCompleted', locale, { n: complete.length })}
              </Button>
            </li>
          ) : null}

          {showCompleted && complete.length > 0 ? (
            <li>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0"
                onClick={() => setShowCompleted(false)}
              >
                {tUi('card.hideCompleted', locale)}
              </Button>
            </li>
          ) : null}

          {visible.map((item) => {
            const done = Boolean(state.checklist[item.key])
            return (
              <li key={item.key}>
                <button
                  type="button"
                  disabled={done}
                  onClick={() => onStepTap?.(item.key)}
                  className={cn(
                    'flex w-full min-h-tap items-start gap-3 rounded-[var(--lc-radius-md)]',
                    'px-2 py-2 text-start focus-visible:outline-none',
                    !done && 'hover:bg-[var(--lc-surface-sunken)]',
                  )}
                >
                  {done ? <span className="sr-only">Completed</span> : null}
                  <span
                    className={cn(
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                      done
                        ? 'border-[var(--lc-status-published-fg)] bg-[var(--lc-status-published-bg)]'
                        : 'border-[var(--lc-border-strong)]',
                    )}
                  >
                    {done ? (
                      <Check
                        className="h-3 w-3 text-[var(--lc-status-published-fg)]"
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'flex flex-wrap items-center gap-2',
                        done && 'text-[var(--lc-text-muted)] line-through',
                        !done && 'text-[var(--lc-text-heading)]',
                      )}
                      style={{ font: 'var(--lc-type-body)' }}
                    >
                      {item.label}
                      {item.optional ? (
                        <Badge variant="outline" className="text-[var(--lc-text-muted)]">
                          {tUi('card.optional', locale)}
                        </Badge>
                      ) : null}
                    </span>
                    <span
                      className="block text-[var(--lc-text-muted)]"
                      style={{ font: 'var(--lc-type-caption)' }}
                    >
                      {item.sub}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}

      {onDismissForever && !allDone ? (
        <div className="mt-[var(--lc-space-md)]">
          <Button type="button" variant="link" className="h-auto p-0" onClick={onDismissForever}>
            {tUi('card.dismiss', locale)}
          </Button>
        </div>
      ) : null}
    </section>
  )
}
